/**
 * Integrations Redesign — Service Layer (INT-03)
 *
 * This service is the single access point for all data operations required
 * by the new Integrations screen and the Team Members Company Access tab.
 *
 * All functions call the live backend API by default.
 * Set VITE_USE_MOCK_DATA=true in your .env to use mock data instead.
 */

import { mockIntegrationsService } from '@/data/mockIntegrationsData';
import { apiClient } from './apiClient';
import type {
  CompanyWithIntegrations,
  CompanyUser,
  EmployeeCompanyAccessRow,
  UpdateIntegrationsPayload,
  TestIntegrationResponse,
} from '@/types';

/**
 * When true, all service functions return mock data instead of calling the API.
 * Driven by the VITE_USE_MOCK_DATA environment variable (defaults to false).
 */
const USE_MOCK = import.meta.env?.VITE_USE_MOCK_DATA === 'true';

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

/**
 * Fetch all companies (enriched with integration details and user count).
 * Sorted by the backend: Active first (alphabetical), then Inactive (alphabetical).
 */
export async function getCompanies(): Promise<CompanyWithIntegrations[]> {
  if (USE_MOCK) {
    return mockIntegrationsService.getCompanies();
  }
  return apiClient.get<CompanyWithIntegrations[]>('/companies');
}

/**
 * Fetch a single company by ID.
 */
export async function getCompany(id: string): Promise<CompanyWithIntegrations | undefined> {
  if (USE_MOCK) {
    return mockIntegrationsService.getCompany(id);
  }
  return apiClient.get<CompanyWithIntegrations>(`/companies/${id}`);
}

/**
 * Create a new company.
 */
export async function createCompany(data: {
  name: string;
  code: string;
  status: 'active' | 'inactive';
}): Promise<CompanyWithIntegrations> {
  if (USE_MOCK) {
    return mockIntegrationsService.createCompany(data);
  }
  return apiClient.post<CompanyWithIntegrations, typeof data>('/companies', data);
}

/**
 * Update company details (name, code, status).
 * Backend uses PUT for full update.
 */
export async function updateCompany(
  id: string,
  data: Partial<Pick<CompanyWithIntegrations, 'name' | 'code' | 'status'>>
): Promise<CompanyWithIntegrations | undefined> {
  if (USE_MOCK) {
    return mockIntegrationsService.updateCompany(id, data);
  }
  return apiClient.put<CompanyWithIntegrations, typeof data>(`/companies/${id}`, data);
}

// ---------------------------------------------------------------------------
// Integrations (INT-I-02, INT-I-06, INT-I-07)
// ---------------------------------------------------------------------------

/**
 * Save Financial, HR, Project, and Planner integration configuration for a company.
 * Corresponds to PUT /companies/{id}/integrations.
 */
export async function updateIntegrations(
  companyId: string,
  payload: UpdateIntegrationsPayload
): Promise<CompanyWithIntegrations | undefined> {
  if (USE_MOCK) {
    return mockIntegrationsService.updateIntegrations(
      companyId,
      payload.financial,
      payload.hr,
      payload.project,
      payload.planner
    );
  }
  return apiClient.put<CompanyWithIntegrations, UpdateIntegrationsPayload>(
    `/companies/${companyId}/integrations`,
    payload
  );
}

/**
 * Trigger an on-demand sync for a specific integration type.
 * Corresponds to POST /companies/{id}/integrations/sync (INT-I-06).
 * Only available when the integration status is 'connected'.
 */
export async function syncIntegration(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner'
): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
  if (USE_MOCK) {
    return mockIntegrationsService.syncIntegration(companyId, type);
  }
  return apiClient.post<
    { success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined },
    { type: 'financial' | 'hr' | 'project' | 'planner' }
  >(`/companies/${companyId}/integrations/sync`, { type });
}

/**
 * Trigger a full re-sync for a specific integration type.
 * Resets change-tracking state and rebuilds all data from scratch.
 * Corresponds to POST /companies/{id}/integrations/resync.
 */
export async function resyncIntegration(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner'
): Promise<{ success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined }> {
  if (USE_MOCK) {
    return mockIntegrationsService.syncIntegration(companyId, type);
  }
  return apiClient.post<
    { success: boolean; message: string; syncedAt: string | null; updatedCompany: CompanyWithIntegrations | undefined },
    { type: 'financial' | 'hr' | 'project' | 'planner' }
  >(`/companies/${companyId}/integrations/resync`, { type });
}

/**
 * Remove a configured integration entirely, resetting it to not_configured.
 * Corresponds to DELETE /companies/{id}/integrations/{type} (INT-I-07).
 */
export async function removeIntegration(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner'
): Promise<CompanyWithIntegrations | undefined> {
  if (USE_MOCK) {
    return mockIntegrationsService.removeIntegration(companyId, type);
  }
  return apiClient.delete<CompanyWithIntegrations>(
    `/companies/${companyId}/integrations/${type}`
  );
}

/**
 * Test the connection for a Financial, HR, Project, or Planner integration.
 * Corresponds to POST /companies/{id}/integrations/test.
 */
export async function testConnection(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner'
): Promise<TestIntegrationResponse> {
  if (USE_MOCK) {
    const result = await mockIntegrationsService.testConnection(companyId, type);
    return {
      success: result.success,
      message: result.message,
      testedAt: new Date().toISOString(),
    };
  }
  return apiClient.post<TestIntegrationResponse, { type: 'financial' | 'hr' | 'project' | 'planner' }>(
    `/companies/${companyId}/integrations/test`,
    { type }
  );
}

// ---------------------------------------------------------------------------
// Company Users — Users with Access section in the sheet (INT-I-03)
// ---------------------------------------------------------------------------

/** Raw response shape from backend where id is an int */
interface CompanyUserApiResponse {
  id: number;
  name: string;
  email: string;
  role: string;
  photoUrl?: string;
  isDefault: boolean;
}

/** Map backend CompanyUserDto (id: int) to frontend CompanyUser (id: string). */
function mapCompanyUser(apiUser: CompanyUserApiResponse): CompanyUser {
  return { ...apiUser, id: String(apiUser.id) };
}

/**
 * Get all users who have access to a given company.
 * Corresponds to GET /companies/{id}/users.
 */
export async function getCompanyUsers(companyId: string): Promise<CompanyUser[]> {
  if (USE_MOCK) {
    return mockIntegrationsService.getCompanyUsers(companyId);
  }
  const response = await apiClient.get<CompanyUserApiResponse[]>(`/companies/${companyId}/users`);
  return response.map(mapCompanyUser);
}

/**
 * Grant a user access to a company.
 * Corresponds to POST /companies/{id}/users.
 * Backend expects { userId: int }.
 */
export async function addUserToCompany(
  companyId: string,
  userId: string
): Promise<CompanyUser | undefined> {
  if (USE_MOCK) {
    return mockIntegrationsService.addUserToCompany(companyId, userId);
  }
  const response = await apiClient.post<CompanyUserApiResponse, { userId: number }>(
    `/companies/${companyId}/users`,
    { userId: parseInt(userId, 10) }
  );
  return mapCompanyUser(response);
}

/**
 * Revoke a user's access to a company.
 * Corresponds to DELETE /companies/{id}/users/{userId}.
 * Backend route constraint requires userId as int.
 */
export async function removeUserFromCompany(
  companyId: string,
  userId: string
): Promise<void> {
  if (USE_MOCK) {
    return mockIntegrationsService.removeUserFromCompany(companyId, userId);
  }
  await apiClient.delete(`/companies/${companyId}/users/${userId}`);
}

/**
 * Search for users that can be added to a company.
 * Returns users not already assigned to the given company.
 * Corresponds to GET /employees/search.
 */
export async function searchUsers(
  query: string,
  excludeCompanyId?: string
): Promise<CompanyUser[]> {
  if (USE_MOCK) {
    return mockIntegrationsService.searchUsers(query, excludeCompanyId);
  }

  const params = new URLSearchParams({ query });
  if (excludeCompanyId) {
    params.set('excludeCompanyId', excludeCompanyId);
  }

  const response = await apiClient.get<CompanyUserApiResponse[]>(
    `/employees/search?${params.toString()}`
  );
  return response.map(mapCompanyUser);
}

/**
 * Fetch all users, optionally excluding those already assigned to a company.
 * Corresponds to GET /employees.
 */
export async function getAllUsers(excludeCompanyId?: string): Promise<CompanyUser[]> {
  if (USE_MOCK) {
    return mockIntegrationsService.getAllUsers(excludeCompanyId);
  }

  const params = new URLSearchParams();
  if (excludeCompanyId) {
    params.set('excludeCompanyId', excludeCompanyId);
  }

  const response = await apiClient.get<CompanyUserApiResponse[]>(
    `/employees?${params.toString()}`
  );
  return response.map(mapCompanyUser);
}

// ---------------------------------------------------------------------------
// Employee Company Access (Team Members → Company Access tab)
// ---------------------------------------------------------------------------

/**
 * Get the full company access list for a given employee.
 * Corresponds to GET /employees/{id}/company-access (INT-I-04).
 */
export async function getEmployeeCompanyAccess(
  employeeId: string
): Promise<EmployeeCompanyAccessRow[]> {
  if (USE_MOCK) {
    return mockIntegrationsService.getEmployeeCompanyAccess(employeeId);
  }
  return apiClient.get<EmployeeCompanyAccessRow[]>(
    `/employees/${employeeId}/company-access`
  );
}

/**
 * Save the full company access list for a given employee.
 * Corresponds to PATCH /employees/{id}/company-access (INT-I-04).
 */
export async function updateEmployeeCompanyAccess(
  employeeId: string,
  updates: Array<{ companyId: string; hasAccess: boolean; isDefault: boolean }>
): Promise<EmployeeCompanyAccessRow[]> {
  if (USE_MOCK) {
    return mockIntegrationsService.updateEmployeeCompanyAccess(employeeId, updates);
  }
  const payload = updates
    .filter((u) => u.hasAccess)
    .map((u) => ({ companyId: u.companyId, isDefault: u.isDefault }));
  return apiClient.patch<EmployeeCompanyAccessRow[], typeof payload>(
    `/employees/${employeeId}/company-access`,
    payload
  );
}

// ---------------------------------------------------------------------------
// Current User Company Access (INT-I-05 / INT-12)
// ---------------------------------------------------------------------------

/**
 * Get the company access list for the currently authenticated user.
 * Corresponds to GET /employees/me/company-access.
 */
export async function getCurrentUserCompanyAccess(): Promise<EmployeeCompanyAccessRow[]> {
  if (USE_MOCK) {
    return mockIntegrationsService.getEmployeeCompanyAccess('current-user');
  }
  return apiClient.get<EmployeeCompanyAccessRow[]>('/employees/me/company-access');
}

// ---------------------------------------------------------------------------
// API Secret Management
// ---------------------------------------------------------------------------

export interface ApiSecretResponse {
  maskedApiSecret: string | null;
}

/**
 * Get the masked API secret for a specific integration type within a company.
 */
export async function getApiSecret(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner'
): Promise<ApiSecretResponse> {
  return apiClient.get<ApiSecretResponse>(
    `/companies/${companyId}/integrations/${type}/api-secret`
  );
}

/**
 * Save or update the API secret for a specific integration type within a company.
 */
export async function saveApiSecret(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner',
  apiSecret: string
): Promise<ApiSecretResponse> {
  return apiClient.put<ApiSecretResponse, { apiSecret: string }>(
    `/companies/${companyId}/integrations/${type}/api-secret`,
    { apiSecret }
  );
}

/**
 * Delete the API secret for a specific integration type within a company.
 */
export async function deleteApiSecret(
  companyId: string,
  type: 'financial' | 'hr' | 'project' | 'planner'
): Promise<void> {
  return apiClient.delete<void>(
    `/companies/${companyId}/integrations/${type}/api-secret`
  );
}
